import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";

import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import DemoForm from "../components/forms/Form";
import * as dayjs from "dayjs";
import EventRow from "../components/EventRow";
import Slideshow from "../components/Slideshow";

import { getCookie } from "cookies-next";

import fonts from "../styles/FontInter.module.css";
import mainTheme from "../components/mainTheme";
import styles from "../styles/Home.module.css";

import client from "../components/client";
import ExitModal from "../components/ExitModal";
import FormMini from "../components/forms/FormMini";

export default function TemplatePage({ utm, events }) {
  useEffect(() => {
    dataLayer.push({ event: "optimize.activate" });
  }, []);

  let eventList = [];
  for (let event of events) {
    const now = dayjs();
    const eventDate =
      dayjs(event.event_date).format("MMM D, YYYY") != "Invalid Date" ? dayjs(event.event_date) : dayjs();

    if (event.event_type === "webinar" && eventList.length < 3 && eventDate > now) {
      eventList.push(event);
    }
  }
  if (eventList.length < 3) {
    for (let event of events) {
      if (event.event_type == "ondemand" && eventList.length < 3) {
        eventList.push(event);
      }
    }
  }

  return (
    <ThemeProvider theme={mainTheme}>
      <Head>
        <title>Individual Agents - Elm Street</title>
        <meta
          name="description"
          content="Save valuable time by consolidating and automating the “busy work” of your day-to-day with Elm Street's comprehensive suite of products and services."
        />
        <link rel="icon" href="/favicon.ico" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@weareelmstreet" />
        <meta name="twitter:creator" content="@weareelmstreet" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://elmstreet.com/agents" />
        <meta property="og:title" content="Individual Agents - Elm Street" />
        <meta
          property="og:description"
          content="Save valuable time by consolidating and automating the “busy work” of your day-to-day with Elm Street's comprehensive suite of products and services."
        />
        <meta property="og:image" content="https://elmstreet.com/og_images/elmstreetog.jpg" />
      </Head>
      <CssBaseline className={fonts} />
      <MainHeader />

      <ExitModal>
        <div
          style={{
            backgroundImage: "url('orangebg.webp')",
            backgroundPosition: "center",
            backgroundSize: "fixed",
            padding: "30px",
            display: "block",
          }}
        >
          <div style={{ textAlign: "center", paddingBottom: "20px" }}>
            {" "}
            <Typography
              variant="h3"
              component="h2"
              style={{ color: "#ffffff", textAlign: "center", paddingBottom: "10px" }}
            >
              Stay up to date...
            </Typography>
            <Typography variant="h5" component="h2" style={{ color: "#333666", textAlign: "center" }}>
              on the latest &amp; greatest in the industry. Be the first to know about:
            </Typography>
            <br />
            <strong>
              + Upcoming webinars with industry leaders
              <br />
              + The latest in marketing &amp; lead gen strategies
              <br />
              + Newly-published industry guides
              <br />
              + First dibs on educational boot camps &amp; seminars
              <br />
            </strong>
          </div>
          <div style={{ backgroundColor: "#ffffff", padding: "25px" }}>
            <FormMini
              buttonText="Register before you go"
              formName="es2_blog"
              listId="147953"
              utmOverride="exit_intent"
            />
          </div>
        </div>
      </ExitModal>

      <div
        style={{
          backgroundImage: "url('agentbg.jpg')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg">
          <Grid container>
            <Grid item md={4} sm={12}>
              <div
                style={{
                  color: "#FFFFFF",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  height: "400px",
                }}
              >
                <div>
                  <Typography variant="h2" component="h1">
                    agent productivity
                    <br />
                    <span style={{ color: mainTheme.palette.secondary.main }}>at its best!</span>
                  </Typography>
                  <p>Meet...Then Exceed Your Goals.</p>
                  <Button variant="contained" color="secondary" href="#form">
                    take a tour
                  </Button>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      <Container maxWidth="lg" style={{ paddingLeft: "20px", paddingRight: "20px" }}>
        <div
          style={{
            marginTop: "75px",
            textAlign: "center",
          }}
        >
          <Grid container spacing={8}>
            <Grid item md={12} sm={12}>
              “Elm Street's given me back my time! They help me work smarter…not harder, so I have more time to focus on
              other things in life. I'm not a very tech-focused person, but my support team made everything so easy, I
              feel like a pro..” - Korrin F.
            </Grid>
          </Grid>
        </div>
      </Container>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "45px",
            paddingBottom: "60px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>work smarter</span>
                  <br />
                  not harder
                </Typography>
                <p>
                  Save valuable time by consolidating and automating the “busy work” of your day-to-day with Elm
                  Street's comprehensive suite of products and services.
                </p>
                <Button variant="contained" color="secondary" href="#form">
                  learn more
                </Button>
              </Grid>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/agents1.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street For Agents - Work Smarter Not Harder"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <Container maxWidth="lg" style={{ marginTop: "45px" }}>
        <Grid container columnSpacing={16} style={{ textAlign: "center" }}>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image src="/analytics_icon.svg" width="100px" height="100px" alt="Elm Street - Aadvanced CRM" />
            </div>
            <strong>
              ADVANCED
              <br />
              CRM
            </strong>
          </Grid>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image src="/check_icon.svg" width="100px" height="100px" alt="Elm Street - Intutive Dashboard" />
            </div>
            <strong>
              INTUITIVE
              <br />
              DASHBOARD
            </strong>
          </Grid>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image src="/responsive_icon.svg" width="100px" height="100px" alt="Elm Street - IDX Websites" />
            </div>
            <strong>
              IDX
              <br />
              WEBSITES
            </strong>
          </Grid>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image src="/social_icon.svg" width="100px" height="100px" alt="Elm Street - Social Media Marketing" />
            </div>
            <strong>
              SOCIAL MEDIA
              <br />
              MARKETING
            </strong>
          </Grid>
        </Grid>

        <Grid container columnSpacing={16} style={{ textAlign: "center" }}>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image src="/blog_icon.svg" width="100px" height="100px" alt="Elm Street - Automated Communication" />
            </div>
            <strong>
              AUTOMATED
              <br />
              COMMUNICATION
            </strong>
          </Grid>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image src="/email_icon.svg" width="100px" height="100px" alt="Elm Street - Direct Mail Newsletters" />
            </div>
            <strong>
              DIRECT MAIL
              <br />
              NEWSLETTERS
            </strong>
          </Grid>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image src="/mobile_icon.svg" width="100px" height="100px" alt="Elm Street - Telecommunication" />
            </div>
            <strong>
              TELECOM
              <br />
              SERVICES
            </strong>
          </Grid>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image src="/lead_icon.svg" width="100px" height="100px" alt="Elm Street - Lead Generation" />
            </div>
            <strong>
              LEAD
              <br />
              GENERATION
            </strong>
          </Grid>
        </Grid>

        <Grid container columnSpacing={16} style={{ textAlign: "center" }}>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image src="/support_icon.svg" width="100px" height="100px" alt="Elm Street - Fanatical Support" />
            </div>
            <strong>
              FANATICAL
              <br />
              SUPPORT
            </strong>
          </Grid>
          <Grid item md={3} sm={12} style={{ paddingBottom: "33px" }}>
            <div style={{ paddingBottom: "13px" }}>
              <Image
                src="/training_icon.svg"
                width="100px"
                height="100px"
                alt="Elm Street - Training &amp; Education"
              />
            </div>
            <strong>
              TRAINING
              <br />
              &amp; EDUCATION
            </strong>
          </Grid>
          <Grid item md={3} sm={12}></Grid>
          <Grid item md={3} sm={12}></Grid>
        </Grid>
      </Container>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "45px",
            paddingBottom: "85px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/teams2.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street For Agents - Work on Your Business"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>
                    work on <br />
                    your business
                  </span>
                  <br />
                  not in it!
                </Typography>

                <p>
                  Elm Street's marketing technology provides busy agents with important insights into the parts and
                  pieces of their business that truly matter, empowering them to make better choices on how to
                  prioritize their time.
                </p>
                <Button variant="contained" color="secondary" href="#form">
                  connect with us
                </Button>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div
        style={{
          backgroundImage: "url('guidebg.webp')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "55px",
            paddingBottom: "95px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h2" component="h2" style={{ color: "#ffffff" }}>
                  <span style={{ color: "#f69c26" }}>free</span>
                  <br />
                  training <br />
                  downloads
                </Typography>
                <span style={{ color: "#ffffff" }}>
                  <br />
                  <strong style={{ color: "#f69c26" }}>WE MAKE LEARNING FUN!</strong>
                  <br />
                  Take your marketing to the next level with one of our helpful, step-by-step guides designed to
                  inspire, education and entertain.
                </span>
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4" style={{ color: "#ffffff" }}>
                  popular <br />
                  <span style={{ color: "#f69c26" }}>topics</span>
                </Typography>
                <p>
                  <strong style={{ color: "#ffffff" }}>
                    - Lead Generation “Secret Sauce” Guide
                    <br />
                    - 6 Marketing Lessons from Kim Kardashian
                    <br />
                    - Social Media 101 Guide
                    <br />
                    - Listings, Listings, Listings
                    <br />
                    - Awesome Email Marketing Guide
                    <br />
                  </strong>
                </p>
                <Button variant="contained" color="secondary" href="/educational_guides">
                  browse all guides
                </Button>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div
        style={{
          backgroundColor: "#f7f7f7",
        }}
      >
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "25px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={5} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>
                    marketing
                    <br />
                    technology
                  </span>{" "}
                  for agents
                </Typography>
                <br />
                <p>
                  Today's agents have high expectations when it comes to the tools, training and resources they entrust
                  to build their business. With Elm Street, agents have one partner delivering an intuitive CRM, lead
                  generation, IDX websites, social media marketing, automated communication workflows, top-of-mind
                  direct mail, phone services, and more...all backed by fanatical support.
                </p>
                <Button variant="contained" color="secondary" href="#form">
                  schedule demo
                </Button>
              </Grid>
              <Grid item md={7} sm={12} style={{ marginBottom: "-6px" }}>
                <Slideshow
                  pages={[
                    <Grid container spacing={8} key="1">
                      <Grid item md={12} sm={12} style={{ marginBottom: "-6px" }}>
                        <div>
                          <Image
                            src="/crm1.png"
                            width="1000px"
                            height="717px"
                            alt="Elm Street CRM - Intuitive Dashboards &amp; Metrics"
                          />
                        </div>
                      </Grid>
                    </Grid>,

                    <Grid container spacing={8} key="2">
                      <Grid item md={12} sm={12} style={{ marginBottom: "-6px" }}>
                        <div>
                          <Image
                            src="/crm2.png"
                            width="1000px"
                            height="717px"
                            alt="Elm Street CRM - Intuitive Dashboards &amp; Metrics"
                          />
                        </div>
                      </Grid>
                    </Grid>,

                    <Grid container spacing={8} key="3">
                      <Grid item md={12} sm={12} style={{ marginBottom: "-6px" }}>
                        <div>
                          <Image
                            src="/crm3.png"
                            width="1000px"
                            height="717px"
                            alt="Elm Street CRM - Intuitive Dashboards &amp; Metrics"
                          />
                        </div>
                      </Grid>
                    </Grid>,

                    <Grid container spacing={8} key="4">
                      <Grid item md={12} sm={12} style={{ marginBottom: "-6px" }}>
                        <div>
                          <Image
                            src="/crm4.png"
                            width="1000px"
                            height="717px"
                            alt="Elm Street CRM - Intuitive Dashboards &amp; Metrics"
                          />
                        </div>
                      </Grid>
                    </Grid>,

                    <Grid container spacing={8} key="5">
                      <Grid item md={12} sm={12} style={{ marginBottom: "-6px" }}>
                        <div>
                          <Image
                            src="/crm5.png"
                            width="1000px"
                            height="717px"
                            alt="Elm Street CRM - Intuitive Dashboards &amp; Metrics"
                          />
                        </div>
                      </Grid>
                    </Grid>,
                  ]}
                  showDots={false}
                />
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "45px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>easy</span>
                  <br />
                  onboarding
                </Typography>
                <p>
                  Getting up and running with your website, CRM, lead generation or social media marketing is quick and
                  easy. Elm Street does the heavy lifting, working with you to ensure that your personal brand is
                  honored just the way you'd like.
                </p>
                <Button variant="contained" color="secondary" href="#form">
                  learn more
                </Button>
              </Grid>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/teams5.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street For Agents - Easy Onboarding"
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "45px",
            paddingBottom: "85px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12}>
                <div>
                  <Image
                    src="/agents5.png"
                    width="1080px"
                    height="1066px"
                    alt="Elm Street For Agents - Training &amp; Fanatical Support"
                  />
                </div>
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  <span style={{ color: "#f69c26" }}>
                    training <br />
                    &amp; fanatical
                  </span>
                  <br />
                  support
                </Typography>

                <p>
                  Technology is only as good as the people behind it. Busy agents need a reliable technology partner
                  that can deliver a smooth experience and continue to train and support when needed.
                </p>
                <Button variant="contained" color="secondary" href="#form">
                  take a tour
                </Button>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div style={{ backgroundColor: "#f7f7f7" }}>
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "55px",
            paddingBottom: "95px",
          }}
        >
          <div style={{ marginTop: "55px" }}>
            <Grid container spacing={8}>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                {eventList.map(function (event) {
                  return <EventRow client={client} event={event} type={event.event_type} key={event._id} />;
                })}
              </Grid>
              <Grid item md={6} sm={12} style={{ paddingBottom: "20px" }}>
                <Typography variant="h4" component="h4">
                  attend an
                  <br />
                  <span style={{ color: "#f69c26" }}>
                    educational <br />
                    webinar
                  </span>
                </Typography>
                <p>
                  Elm Street Academy offers high-energy, empowering marketing and technology education, both online and
                  in-person. Popular education sessions include social media marketing, online lead generation, building
                  repeat and referral business, business planning, and more.
                </p>
                <Button variant="contained" color="secondary" href="/events">
                  browse all upcoming events
                </Button>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      <div
        style={{
          backgroundImage: "url('horizonbg.webp')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container
          maxWidth="lg"
          style={{
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "105px",
            paddingBottom: "65px",
          }}
        >
          <Grid container columnSpacing={16}>
            <Grid item md={4} sm={12} style={{ paddingBottom: "24px" }}>
              <span style={{ color: "#ffffff" }}>
                “Elm Street's website and marketing services have helped me stand out from the crowd, without having to
                put a lot of effort into it.”
                <br />
                <strong style={{ color: "#f69c26" }}>Sheila R.</strong>
                <br />
                Galveston, TX
              </span>
            </Grid>
            <Grid item md={4} sm={12} style={{ paddingBottom: "24px" }}>
              <span style={{ color: "#ffffff" }}>
                “I receive lots of compliments on my digital and print newsletters. My clients love that I go above and
                beyond to stay in touch.”
                <br />
                <strong style={{ color: "#f69c26" }}>Kevin D.</strong>
                <br />
                Cleveland, OH
              </span>
            </Grid>
            <Grid item md={4} sm={12} style={{ paddingBottom: "24px" }}>
              <span style={{ color: "#ffffff" }}>
                “Elm Street's Concierge is the best lead outreach service I've ever used. They do the work, I make the
                money!”
                <br />
                <strong style={{ color: "#f69c26" }}>Pam W.</strong>
                <br />
                Portland, OR
              </span>
            </Grid>
          </Grid>
        </Container>
      </div>

      <div
        style={{
          backgroundImage: "url('orangebg.webp')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          display: "block",
        }}
      >
        <Container maxWidth="lg" style={{ paddingTop: "80px", paddingBottom: "85px" }}>
          <div style={{}}>
            <a id="form" />
            <Typography variant="h4" component="h4" style={{ color: "#1d1746", textAlign: "center" }}>
              schedule a demo
            </Typography>
            <div style={{ paddingBottom: "40px", textAlign: "center" }}>
              <strong style={{ color: "#1d1746" }}>
                Connect with Elm Street's Success Team today to explore your path to success.
              </strong>
            </div>
            <div style={{ padding: "40px", backgroundColor: "#ffffff" }}>
              <Container
                maxWidth="lg"
                style={{
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  marginTop: "65px",
                  marginBottom: "65px",
                }}
              >
                <Grid container>
                  <Grid item md={2} sm={12} xs={12} />
                  <Grid item md={8} sm={12} xs={12}>
                    <DemoForm
                      buttonText="Schedule Demo"
                      successUrl="thankyou"
                      listId="147997"
                      formName="es2_agents"
                      fieldList={["first_name", "last_name", "email", "phone"]}
                    />
                  </Grid>
                </Grid>
              </Container>
            </div>
          </div>
        </Container>
      </div>

      <MainFooter />
    </ThemeProvider>
  );
}

export async function getServerSideProps({ req, res }) {
  let cookies = getCookie("elmlead", { req, res });

  const eventQuery = `
  *[_type == "event" &&  !("Training" in event_category[]->title)] | order(event_date asc) {
    _id,
    title,
    event_date,
    event_type,
    event_category[]->{
      title
    },
    duration,
    description,
    thumbnail,
    reg_url,
    tags[]->{
      tag
    }
  }
  `;

  const events = await client.fetch(eventQuery);

  if (cookies) {
    return { props: { utm: cookies, events } };
  }
  return { props: { events } };
}
